import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { type AdminUserSummary, isApiError, useRevokeUserSessions } from "@/lib/api";
import { logger } from "@/lib/logger";
import { useUser } from "@/lib/user-context";

interface UserRevokeSessionsDialogProps {
  user: AdminUserSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserRevokeSessionsDialog({
  user,
  open,
  onOpenChange,
}: UserRevokeSessionsDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        {/* The content unmounts on close, so every open starts with fresh
            error/done state without a reset effect. */}
        <RevokeSessionsBody user={user} onClose={() => onOpenChange(false)} />
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface RevokeSessionsBodyProps {
  user: AdminUserSummary | null;
  onClose: () => void;
}

function RevokeSessionsBody({ user, onClose }: RevokeSessionsBodyProps) {
  const { user: currentUser } = useUser();
  const revokeSessions = useRevokeUserSessions();
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const revoking = revokeSessions.isPending;

  // The backend refuses self-revocation (400); change password covers it.
  // Case-insensitive, like the backend's self check on the looked-up email.
  const isSelf =
    !!currentUser?.email && currentUser.email.toLowerCase() === user?.email?.toLowerCase();

  const handleRevoke = async () => {
    if (!user || isSelf) {
      return;
    }
    setError("");

    try {
      await revokeSessions.mutateAsync({ userId: user.id });
      logger.debug("[user-revoke-sessions] signed out everywhere:", user.email);
      setDone(true);
    } catch (err) {
      logger.error("[user-revoke-sessions] failed:", err);
      const detail =
        isApiError(err) && err.body && typeof err.body === "object" && "detail" in err.body
          ? String((err.body as Record<string, unknown>).detail)
          : "Failed to sign the user out. Please try again.";
      setError(detail);
    }
  };

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>Sign out everywhere</AlertDialogTitle>
        <AlertDialogDescription>
          {done ? (
            <>
              All sessions for <strong>{user?.email}</strong> have ended.
            </>
          ) : isSelf ? (
            <>
              You can&apos;t sign yourself out everywhere here. Use Sign out, or change your
              password to end your other sessions.
            </>
          ) : (
            <>
              Sign <strong>{user?.email}</strong> out of every device? Their browser sessions and
              connected MCP clients stop working immediately; they can sign in again.
            </>
          )}
        </AlertDialogDescription>
      </AlertDialogHeader>

      {error && <p className="text-destructive text-sm">{error}</p>}

      <AlertDialogFooter>
        {done ? (
          <AlertDialogAction onClick={onClose}>Close</AlertDialogAction>
        ) : isSelf ? (
          <AlertDialogCancel>Close</AlertDialogCancel>
        ) : (
          <>
            <AlertDialogCancel disabled={revoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={(e) => {
                e.preventDefault();
                void handleRevoke();
              }}
              disabled={revoking}
            >
              {revoking ? "Signing out..." : "Sign out everywhere"}
            </AlertDialogAction>
          </>
        )}
      </AlertDialogFooter>
    </>
  );
}
